import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  Calculator,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { Link } from "react-router";

import {
  type CarrierMeResponse,
  type CarrierRateBandRow,
  type CarrierTransportServiceRow,
  type Decimalish,
  type CarrierServiceAreaRow,
  type PickupRateBandRow,
  type PlatformFeeRow,
  type PricingRow,
  type RedistributionRateBandRow,
  useCarrierAddOnServicesForPricing,
  useCarrierCostRates,
  useDeleteCarrierRateBand,
  useCarrierMe,
  useCarrierPricingOverview,
  useCarrierServiceAreas,
  useCarrierSurcharges,
  useCarrierTransportServices,
  usePreviewCarrierPickupQuote,
} from "@/api/carrier";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  EditRateBandDialog,
  type RateBandDefaults,
} from "@/features/edit-rate-band-dialog";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { hasHttpStatus } from "@/lib/api-error";

/**
 * Carrier pricing. Carrier Admin only (server enforces; DISPATCHER callers see
 * a generic "Failed to load" inline).
 *
 * The carrier owns its label rates and edits them here directly. Shipping and
 * add-on catalogs live under Services so this workspace can stay focused on
 * one quote scope. Saved rates are live for the next quote; the server's
 * guardrails (rate floor, bracket order, overlap) are what stand between a typo
 * and a mispriced checkout.
 *
 * Read-only here: pickup and redistribution pricing, which EZX sets. Add-on
 * services are EZX's too, and so is the EZX fee — that one is
 * our commission, shown so the carrier can see what it earns, never edited by
 * them.
 */
export default function PricingScreen() {
  const carrierMe = useCarrierMe();
  const services = useCarrierTransportServices();
  const addOns = useCarrierAddOnServicesForPricing();
  const serviceAreas = useCarrierServiceAreas({ all: true });

  const serviceRows = useMemo(() => services.data ?? [], [services.data]);
  const pickupRows = useMemo(
    () =>
      (addOns.data ?? []).filter(
        (row) => readRowString(row, ["type"]) === "PICKUP"
      ),
    [addOns.data]
  );
  const dispatchCenters = useMemo(
    () => getDispatchCenters(carrierMe.data?.carriers ?? []),
    [carrierMe.data?.carriers]
  );
  const areaRows = useMemo(
    () => getServiceAreaRows(serviceAreas.data),
    [serviceAreas.data]
  );

  const [carrierServiceId, setCarrierServiceId] = useState("");
  const [pickupServiceId, setPickupServiceId] = useState("");
  const [dispatchCenterId, setDispatchCenterId] = useState("");
  const [fromServiceAreaId, setFromServiceAreaId] = useState("");
  const [toServiceAreaId, setToServiceAreaId] = useState("");
  // null = closed. { band: null } = creating. { band } = editing.
  const [editBand, setEditBand] = useState<{
    band: CarrierRateBandRow | null;
    defaults?: RateBandDefaults;
  } | null>(null);

  useEffect(() => {
    ensureSelected(serviceRows, carrierServiceId, setCarrierServiceId);
  }, [carrierServiceId, serviceRows]);

  useEffect(() => {
    ensureSelected(pickupRows, pickupServiceId, setPickupServiceId);
  }, [pickupRows, pickupServiceId]);

  useEffect(() => {
    ensureSelected(dispatchCenters, dispatchCenterId, setDispatchCenterId);
  }, [dispatchCenters, dispatchCenterId]);

  useEffect(() => {
    ensureSelected(areaRows, fromServiceAreaId, setFromServiceAreaId);
  }, [areaRows, fromServiceAreaId]);

  useEffect(() => {
    ensureSelected(areaRows, toServiceAreaId, setToServiceAreaId);
  }, [areaRows, toServiceAreaId]);

  const selectedCarrierService =
    serviceRows.find((row) => row.id === carrierServiceId) ?? null;
  // Older servers omitted this field; preserve the existing editable behavior
  // until the explicit API-procured value (`false`) arrives.
  const selectedServiceSelfRated =
    selectedCarrierService !== null &&
    selectedCarrierService.selfRated !== false;
  const selectedCarrierName =
    selectedCarrierService?.carrier?.name ?? "Carrier";
  const selectedPickupService =
    pickupRows.find((row) => row.id === pickupServiceId) ?? null;
  const selectedDispatchCenter =
    dispatchCenters.find((row) => row.id === dispatchCenterId) ?? null;
  const destinationCarrierId =
    getCarrierId(selectedCarrierService) ??
    carrierMe.data?.carriers[0]?.id ??
    "";

  const scopeError =
    services.error ?? addOns.error ?? carrierMe.error ?? serviceAreas.error;

  return (
    <div className="carrier-page">
      <PageHeader title="Rate workspace" />

      <ScopeCard
        services={serviceRows}
        servicesLoading={services.isLoading}
        serviceId={carrierServiceId}
        onServiceChange={setCarrierServiceId}
        pickupServices={pickupRows}
        pickupLoading={addOns.isLoading}
        pickupServiceId={pickupServiceId}
        onPickupChange={setPickupServiceId}
        dispatchCenters={dispatchCenters}
        dispatchLoading={carrierMe.isLoading}
        dispatchCenterId={dispatchCenterId}
        onDispatchChange={setDispatchCenterId}
        summary={<PricingOverviewBadges />}
      />

      {scopeError && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          <span>
            {hasHttpStatus(scopeError, 403)
              ? "You do not have permission to load this pricing scope."
              : "Some pricing scope options could not be loaded."}
          </span>
          {!hasHttpStatus(scopeError, 403) && (
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => {
                void services.refetch();
                void addOns.refetch();
                void carrierMe.refetch();
                void serviceAreas.refetch();
              }}
            >
              Retry scope
            </Button>
          )}
        </div>
      )}

      {!selectedCarrierService ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Pick a carrier service above to inspect its rates.
          </CardContent>
        </Card>
      ) : (
        <>
          <RateWorkspacePanel
            carrierId={destinationCarrierId}
            carrierName={selectedCarrierName}
            selfRated={selectedServiceSelfRated}
            carrierServiceId={carrierServiceId}
            pickupServiceId={pickupServiceId}
            dispatchCenterId={dispatchCenterId}
            onEditBand={(band, defaults) => setEditBand({ band, defaults })}
          />

          <ServicePropertiesPanel
            service={selectedCarrierService}
            pickupService={selectedPickupService}
            dispatchCenter={selectedDispatchCenter}
          />

          <QuotePreviewPanel
            carrierServiceId={carrierServiceId}
            pickupServiceId={pickupServiceId}
            dispatchCenterId={dispatchCenterId}
            destinationCarrierId={destinationCarrierId}
            serviceAreas={areaRows}
            serviceAreasLoading={serviceAreas.isLoading}
            fromServiceAreaId={fromServiceAreaId}
            onFromServiceAreaChange={setFromServiceAreaId}
            toServiceAreaId={toServiceAreaId}
            onToServiceAreaChange={setToServiceAreaId}
          />
        </>
      )}

      {editBand && selectedCarrierService && (
        <EditRateBandDialog
          open
          onOpenChange={(next) => {
            if (!next) setEditBand(null);
          }}
          band={editBand.band}
          carrierServiceId={carrierServiceId}
          serviceAreas={areaRows}
          defaults={editBand.defaults}
          serviceLabel={formatServiceLabel(selectedCarrierService)}
          dispatchCenterLabel={selectedDispatchCenter?.name}
          dimFactorLabel={formatUnitValue(
            selectedCarrierService,
            "dimFactorCm",
            "cm³/kg"
          )}
        />
      )}
    </div>
  );
}

function LoadingRow({ label }: { label: string }) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 text-sm text-muted-foreground"
    >
      <Loader2 className="w-4 h-4 animate-spin" />
      Loading {label}...
    </div>
  );
}

function ErrorRow({
  label,
  error,
  onRetry,
}: {
  label: string;
  error: unknown;
  onRetry?: () => void;
}) {
  const forbidden = hasHttpStatus(error, 403);
  return (
    <div role="alert" className="flex items-center gap-3 text-sm">
      <span className="text-destructive">
        {forbidden
          ? `You do not have permission to view ${label}.`
          : `Failed to load ${label}.`}
      </span>
      {onRetry && !forbidden && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

function ScopeCard({
  services,
  servicesLoading,
  serviceId,
  onServiceChange,
  pickupServices,
  pickupLoading,
  pickupServiceId,
  onPickupChange,
  dispatchCenters,
  dispatchLoading,
  dispatchCenterId,
  onDispatchChange,
  summary,
}: {
  services: CarrierTransportServiceRow[];
  servicesLoading: boolean;
  serviceId: string;
  onServiceChange: (value: string) => void;
  pickupServices: PricingRow[];
  pickupLoading: boolean;
  pickupServiceId: string;
  onPickupChange: (value: string) => void;
  dispatchCenters: DispatchCenterOption[];
  dispatchLoading: boolean;
  dispatchCenterId: string;
  onDispatchChange: (value: string) => void;
  summary?: ReactNode;
}) {
  return (
    <Card className="sticky top-0 z-20 grid grid-cols-2 gap-2 p-3 shadow-md shadow-zinc-200/40 lg:flex lg:flex-wrap lg:items-end">
      <span className="col-span-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground lg:mr-1 lg:self-center">
        Scope
      </span>
      <div className="col-span-2 min-w-0 space-y-1 lg:w-[230px]">
        <span className="text-[11px] font-medium text-muted-foreground lg:sr-only">
          Carrier service
        </span>
        {services.length > 0 ? (
          <Select value={serviceId} onValueChange={onServiceChange}>
            <SelectTrigger
              aria-label="Carrier service"
              size="sm"
              className="w-full text-sm"
            >
              <SelectValue placeholder="Pick a carrier service" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Available services</SelectLabel>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {formatServiceLabel(service)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        ) : (
          <EmptySelectLike
            label={
              servicesLoading
                ? "Loading carrier services..."
                : "No carrier services"
            }
          />
        )}
      </div>

      {pickupServices.length > 0 || pickupLoading ? (
        <>
          <span className="hidden self-center text-muted-foreground lg:inline">
            ·
          </span>
          <div className="min-w-0 space-y-1 lg:w-[180px]">
            <span className="text-[11px] font-medium text-muted-foreground lg:sr-only">
              Pickup service
            </span>
            {pickupServices.length > 0 ? (
              <Select value={pickupServiceId} onValueChange={onPickupChange}>
                <SelectTrigger
                  size="sm"
                  className="w-full text-sm"
                  aria-label="Pickup service"
                >
                  <SelectValue placeholder="Pick pickup service" />
                </SelectTrigger>
                <SelectContent>
                  {pickupServices.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {formatServiceLabel(service)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <EmptySelectLike
                label={
                  pickupLoading
                    ? "Loading pickup services..."
                    : "No pickup services"
                }
              />
            )}
          </div>
        </>
      ) : null}

      {dispatchCenters.length > 0 || dispatchLoading ? (
        <>
          <span className="hidden self-center text-muted-foreground lg:inline">
            ·
          </span>
          <div className="min-w-0 space-y-1 lg:w-[180px]">
            <span className="text-[11px] font-medium text-muted-foreground lg:sr-only">
              Dispatch Center
            </span>
            {dispatchCenters.length > 0 ? (
              <Select value={dispatchCenterId} onValueChange={onDispatchChange}>
                <SelectTrigger
                  size="sm"
                  className="w-full text-sm"
                  aria-label="Dispatch Center"
                >
                  <SelectValue placeholder="Pick dispatch center" />
                </SelectTrigger>
                <SelectContent>
                  {dispatchCenters.map((center) => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <EmptySelectLike
                label={
                  dispatchLoading
                    ? "Loading dispatch centers..."
                    : "No dispatch centers"
                }
              />
            )}
          </div>
        </>
      ) : null}
      {summary && (
        <div className="col-span-2 min-w-0 lg:ml-auto lg:self-center">
          {summary}
        </div>
      )}
    </Card>
  );
}

function EmptySelectLike({ label }: { label: string }) {
  return (
    <div className="flex h-11 w-fit min-w-36 items-center rounded-md border border-input bg-background px-3 text-sm text-muted-foreground shadow-sm md:h-8">
      {label}
    </div>
  );
}

function ServicePropertiesPanel({
  service,
  pickupService,
  dispatchCenter,
}: {
  service: PricingRow | null;
  pickupService: PricingRow | null;
  dispatchCenter: DispatchCenterOption | null;
}) {
  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Service properties
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {!service ? (
          <p className="text-sm text-muted-foreground">
            Pick a carrier service above to inspect the pricing scope.
          </p>
        ) : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
            <PropertyValue
              label="Carrier service"
              value={formatServiceLabel(service)}
            />
            <PropertyValue
              label="Pickup service"
              value={pickupService ? formatServiceLabel(pickupService) : "-"}
            />
            <PropertyValue
              label="Dispatch center"
              value={dispatchCenter?.name ?? "-"}
            />
            <PropertyValue
              label="Dim factor"
              value={formatUnitValue(service, "dimFactorCm", "cm³/kg")}
            />
            <PropertyValue
              label="Dim factor (imperial)"
              value={formatUnitValue(service, "dimFactorInch", "in³/lb")}
            />
            <PropertyValue
              label="Estimated transit"
              value={formatUnitValue(service, "estimatedDays", "days")}
            />
            <PropertyValue
              label="Max weight"
              value={formatUnitValue(service, "maxWeightKg", "kg")}
            />
            <PropertyValue
              label="Max longest side"
              value={formatUnitValue(service, "maxLongestSideCm", "cm")}
            />
            <PropertyValue
              label="Max length + girth"
              value={formatUnitValue(service, "maxLengthPlusGirthCm", "cm")}
            />
            <PropertyValue
              label="Effective state"
              value={
                readRowString(service, ["isActive"]) === "true"
                  ? "Active"
                  : "Inactive"
              }
            />
            <PropertyValue
              label="Pricing source"
              value={
                readRowString(service, ["selfRated"]) === "false"
                  ? "Live carrier API"
                  : "Carrier rate bands"
              }
            />
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function PropertyValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-sm" title={value}>
        {value}
      </dd>
    </div>
  );
}

function RateWorkspacePanel({
  carrierId,
  carrierName,
  selfRated,
  carrierServiceId,
  pickupServiceId,
  dispatchCenterId,
  onEditBand,
}: {
  carrierId: string;
  carrierName: string;
  selfRated: boolean;
  carrierServiceId: string;
  pickupServiceId: string;
  dispatchCenterId: string;
  onEditBand: (
    band: CarrierRateBandRow | null,
    defaults?: RateBandDefaults
  ) => void;
}) {
  return (
    <div className="space-y-3">
      <RatesTab
        carrierId={carrierId}
        carrierName={carrierName}
        selfRated={selfRated}
        carrierServiceId={carrierServiceId}
        pickupServiceId={pickupServiceId}
        dispatchCenterId={dispatchCenterId}
        onEditBand={onEditBand}
      />
      <SurchargesTab />
    </div>
  );
}

function PricingOverviewBadges() {
  const { data, isLoading, error, refetch } = useCarrierPricingOverview();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading summary...
      </div>
    );
  }

  if (error) {
    return (
      <Button variant="outline" size="xs" onClick={() => refetch()}>
        Retry summary
      </Button>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-wrap justify-end gap-2">
      <Badge variant="outline">{data.transportServices} services</Badge>
      <Badge variant="outline">{data.costRateBands} rate bands</Badge>
      <Badge variant="outline">{data.surcharges} surcharges</Badge>
    </div>
  );
}

function RatesTab({
  carrierId,
  carrierName,
  selfRated,
  carrierServiceId,
  pickupServiceId,
  dispatchCenterId,
  onEditBand,
}: {
  carrierId: string;
  carrierName: string;
  selfRated: boolean;
  carrierServiceId: string;
  pickupServiceId: string;
  dispatchCenterId: string;
  onEditBand: (
    band: CarrierRateBandRow | null,
    defaults?: RateBandDefaults
  ) => void;
}) {
  const { data, isLoading, error, refetch } = useCarrierCostRates();
  if (isLoading) return <LoadingRow label="cost rates" />;
  if (error) {
    return (
      <ErrorRow label="cost rates" error={error} onRetry={() => refetch()} />
    );
  }
  if (!data) return null;

  const labelBands = data.labelBands.filter(
    (row) => row.carrierServiceId === carrierServiceId
  );
  const labelMatrix = buildLabelMatrix(labelBands);
  const unpricedLaneBrackets = labelMatrix.lanes.reduce(
    (total, lane) =>
      total +
      labelMatrix.columns.filter(
        (column) => !labelMatrix.cells.has(`${lane.key}:${column.key}`)
      ).length,
    0
  );
  const pickupBands = data.pickupBands.filter(
    (row) =>
      row.carrierAddOnService?.id === pickupServiceId &&
      row.dispatchCenter?.id === dispatchCenterId
  );
  const redistributionBands = data.redistributionBands.filter(
    (row) =>
      row.carrierId === carrierId && row.dispatchCenter?.id === dispatchCenterId
  );

  return (
    <div className="space-y-3">
      {selfRated && unpricedLaneBrackets > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
          <div className="text-sm">
            <strong className="font-semibold text-amber-800 dark:text-amber-200">
              {unpricedLaneBrackets} configured lane bracket
              {unpricedLaneBrackets === 1 ? " is" : "s are"} unpriced.
            </strong>
            <p className="mt-0.5 text-muted-foreground">
              A matching shipment in one of those weight brackets receives no
              carrier option. Empty is not $0, and label rates never fall back
              to another lane.
            </p>
          </div>
        </div>
      )}
      {selfRated ? (
        <LabelBandsTable rows={labelBands} onEditBand={onEditBand} />
      ) : (
        <LabelApiNotice carrierName={carrierName} />
      )}
      <div className="grid items-start gap-3 xl:grid-cols-2">
        <RedistributionBandsTable rows={redistributionBands} />
        <PickupBandsTable rows={pickupBands} />
      </div>
    </div>
  );
}

function Lane({ from, to }: { from: { name: string }; to?: { name: string } }) {
  return (
    <span className="whitespace-nowrap">
      {from.name}
      {to !== undefined && (
        <>
          <span className="text-muted-foreground"> → </span>
          {to.name}
        </>
      )}
    </span>
  );
}

function Weight({ from, to }: { from: Decimalish; to: Decimalish }) {
  return (
    <span className="whitespace-nowrap tabular-nums">
      {Number(from)}–{Number(to)} kg
    </span>
  );
}

function Money({ cents }: { cents: number | null | undefined }) {
  if (cents == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <span className="tabular-nums">{formatCents(cents)}</span>;
}

function TableShell({
  title,
  note,
  action,
  head,
  mobile,
  children,
}: {
  title: string;
  note?: string;
  action?: ReactNode;
  head: ReactNode;
  mobile?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          {note && <p className="text-xs text-muted-foreground">{note}</p>}
        </div>
        {action}
      </div>
      <CardContent className="p-0">
        {mobile && <div className="divide-y md:hidden">{mobile}</div>}
        <div
          className={
            mobile ? "hidden overflow-x-auto md:block" : "overflow-x-auto"
          }
        >
          <Table className="[&_td]:px-4 [&_th]:px-4">
            <TableHeader>
              <TableRow className="hover:bg-transparent">{head}</TableRow>
            </TableHeader>
            <TableBody>{children}</TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyRow({ span, label }: { span: number; label: string }) {
  return (
    <TableRow>
      <TableCell colSpan={span} className="text-sm text-muted-foreground">
        {label}
      </TableCell>
    </TableRow>
  );
}

function MobileEmpty({ label }: { label: string }) {
  return <p className="p-4 text-sm text-muted-foreground">{label}</p>;
}

interface LabelMatrixColumn {
  key: string;
  weightFromKg: number;
  weightToKg: number;
}

interface LabelMatrixLane {
  key: string;
  from: CarrierRateBandRow["fromServiceArea"];
  to: CarrierRateBandRow["toServiceArea"];
  label: string;
}

const labelLaneKey = (
  from: CarrierRateBandRow["fromServiceArea"],
  to: CarrierRateBandRow["toServiceArea"]
) => JSON.stringify([from.id, to.id]);

const labelWeightKey = (weightFromKg: number, weightToKg: number) =>
  JSON.stringify([weightFromKg, weightToKg]);

const weightRangeLabel = (weightFromKg: number, weightToKg: number) =>
  `${weightFromKg}–${weightToKg} kg`;

function buildLabelMatrix(rows: CarrierRateBandRow[]) {
  const columnsByKey = new Map<string, LabelMatrixColumn>();
  const lanesByKey = new Map<string, LabelMatrixLane>();
  const cells = new Map<string, CarrierRateBandRow>();

  for (const row of rows) {
    const weightFromKg = Number(row.weightFromKg);
    const weightToKg = Number(row.weightToKg);
    const columnKey = labelWeightKey(weightFromKg, weightToKg);
    const laneKey = labelLaneKey(row.fromServiceArea, row.toServiceArea);

    columnsByKey.set(columnKey, {
      key: columnKey,
      weightFromKg,
      weightToKg,
    });
    lanesByKey.set(laneKey, {
      key: laneKey,
      from: row.fromServiceArea,
      to: row.toServiceArea,
      label: `${row.fromServiceArea.name} → ${row.toServiceArea.name}`,
    });
    cells.set(`${laneKey}:${columnKey}`, row);
  }

  const columns = [...columnsByKey.values()].sort(
    (a, b) => a.weightFromKg - b.weightFromKg || a.weightToKg - b.weightToKg
  );
  const lanes = [...lanesByKey.values()].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  return { cells, columns, lanes };
}

/** Label cost bands — the carrier's own rate card, and the only editable one. */
function LabelApiNotice({ carrierName }: { carrierName: string }) {
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <Badge variant="outline" className="shrink-0 font-normal">
          {carrierName} API
        </Badge>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">
            Nothing to configure per lane.
          </span>{" "}
          The label price is fetched live from {carrierName} at quote time, per
          parcel. There are no label rate bands to edit; pickup, redistribution,
          and EZX fee details remain visible below.
        </p>
      </div>
    </Card>
  );
}

function LabelBandsTable({
  rows,
  onEditBand,
}: {
  rows: CarrierRateBandRow[];
  onEditBand: (
    band: CarrierRateBandRow | null,
    defaults?: RateBandDefaults
  ) => void;
}) {
  const remove = useDeleteCarrierRateBand();
  const matrix = buildLabelMatrix(rows);
  const deleteLock = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    band: CarrierRateBandRow;
    lane: string;
    bracket: string;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);

  const requestDelete = (
    band: CarrierRateBandRow,
    lane: string,
    bracket: string
  ) => {
    setActionError(null);
    setActionStatus(null);
    setDeleteTarget({ band, lane, bracket });
  };

  const confirmDelete = () => {
    if (!deleteTarget || deleteLock.current) return;
    const target = deleteTarget;
    deleteLock.current = true;
    remove.mutate(target.band.id, {
      onSuccess: () => {
        setActionStatus(`${target.lane}, ${target.bracket} deleted.`);
        setDeleteTarget(null);
      },
      onError: () => {
        setActionError(
          `${target.lane}, ${target.bracket} could not be deleted. Please retry.`
        );
      },
      onSettled: () => {
        deleteLock.current = false;
      },
    });
  };

  return (
    <>
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
          <div className="mr-auto min-w-0">
            <div className="text-sm font-semibold">Label rates</div>
            <p className="text-xs text-muted-foreground">
              Package billable-weight bands · Base / shipment
            </p>
          </div>
          <Button
            size="sm-touch"
            variant="outline"
            onClick={() => onEditBand(null)}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add band
          </Button>
        </div>

        {actionError && (
          <p
            role="alert"
            className="border-b border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive"
          >
            {actionError}
          </p>
        )}
        {actionStatus && (
          <p role="status" className="border-b bg-muted/40 px-4 py-2 text-sm">
            {actionStatus}
          </p>
        )}

        <CardContent className="p-0">
          <div className="divide-y md:hidden">
            {rows.length === 0 ? (
              <MobileEmpty label="No label rates yet. Add a band to start quoting." />
            ) : (
              matrix.lanes.map((lane) => {
                const gaps = matrix.columns.filter(
                  (column) => !matrix.cells.has(`${lane.key}:${column.key}`)
                ).length;
                return (
                  <section
                    key={lane.key}
                    aria-label={`Rates for ${lane.label}`}
                    className="p-3"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <h3 className="min-w-0 truncate text-sm font-medium">
                        {lane.label}
                      </h3>
                      {gaps > 0 && (
                        <StatusBadge
                          status="ATTENTION"
                          label={`${gaps} unpriced`}
                          className="shrink-0"
                        />
                      )}
                    </div>
                    <div className="divide-y rounded-md border">
                      {matrix.columns.map((column) => {
                        const band = matrix.cells.get(
                          `${lane.key}:${column.key}`
                        );
                        const bracket = weightRangeLabel(
                          column.weightFromKg,
                          column.weightToKg
                        );
                        return (
                          <div
                            key={column.key}
                            className="grid min-h-14 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-xs text-muted-foreground">
                                {bracket}
                              </p>
                              {band ? (
                                <BandPriceSummary band={band} />
                              ) : (
                                <p className="text-xs text-amber-700">
                                  No price configured
                                </p>
                              )}
                            </div>
                            {band ? (
                              <div className="flex">
                                <Button
                                  type="button"
                                  size="icon-touch"
                                  variant="ghost"
                                  aria-label={`Edit rate band for ${lane.label}, ${bracket}`}
                                  onClick={() => onEditBand(band)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="icon-touch"
                                  variant="ghost"
                                  aria-label={`Delete rate band for ${lane.label}, ${bracket}`}
                                  disabled={remove.isPending}
                                  onClick={() =>
                                    requestDelete(band, lane.label, bracket)
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm-touch"
                                aria-label={`Add rate for ${lane.label.replace(" → ", " to ")}, ${bracket}`}
                                onClick={() =>
                                  onEditBand(null, {
                                    fromServiceAreaId: lane.from.id,
                                    toServiceAreaId: lane.to.id,
                                    weightFromKg: column.weightFromKg,
                                    weightToKg: column.weightToKg,
                                  })
                                }
                              >
                                <Plus className="h-4 w-4" />
                                Add
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })
            )}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <Table
              aria-label="Label rate matrix"
              className="[&_td]:px-4 [&_th]:px-4"
            >
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[220px] align-bottom py-2">
                    Lane
                  </TableHead>
                  {matrix.columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className="min-w-48 whitespace-nowrap py-2 align-bottom"
                    >
                      {weightRangeLabel(column.weightFromKg, column.weightToKg)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <EmptyRow
                    span={1}
                    label="No label rates yet. Add a band to start quoting."
                  />
                ) : (
                  matrix.lanes.map((lane) => {
                    const gaps = matrix.columns.filter(
                      (column) => !matrix.cells.has(`${lane.key}:${column.key}`)
                    ).length;

                    return (
                      <TableRow key={lane.key} className="hover:bg-transparent">
                        <TableCell className="py-2 align-top font-medium">
                          <div className="flex flex-col gap-1">
                            <Lane from={lane.from} to={lane.to} />
                            {gaps > 0 && (
                              <div className="flex flex-wrap items-center gap-1.5">
                                <StatusBadge
                                  status="ATTENTION"
                                  label={`${gaps} unpriced`}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        {matrix.columns.map((column) => {
                          const band = matrix.cells.get(
                            `${lane.key}:${column.key}`
                          );
                          const bracket = weightRangeLabel(
                            column.weightFromKg,
                            column.weightToKg
                          );
                          const endpointCents = band
                            ? band.baseRateCents +
                              Math.round(
                                Math.max(
                                  0,
                                  column.weightToKg -
                                    Number(
                                      band.anchorWeightKg ?? band.weightFromKg
                                    )
                                ) * (band.perKgCents ?? 0)
                              )
                            : 0;

                          return (
                            <TableCell
                              key={column.key}
                              className="py-2 align-top"
                            >
                              {band ? (
                                <div className="group relative min-w-40 pr-9">
                                  <div className="flex items-center gap-1.5 text-xs">
                                    <span className="font-medium">
                                      <Money cents={band.baseRateCents} />
                                    </span>
                                    {band.perKgCents != null && (
                                      <>
                                        <span className="text-muted-foreground">
                                          +
                                        </span>
                                        <Money cents={band.perKgCents} />
                                        <span className="text-muted-foreground">
                                          /kg
                                        </span>
                                      </>
                                    )}
                                    <span className="text-[10px] text-muted-foreground">
                                      {band.currencyCode}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[10px] text-muted-foreground">
                                    Base / shipment at{" "}
                                    {Number(
                                      band.anchorWeightKg ?? band.weightFromKg
                                    )}
                                    kg
                                    {band.perKgCents != null &&
                                      band.perKgCents !== 0 && (
                                        <>
                                          {" · example "}
                                          {formatCents(endpointCents)} at{" "}
                                          {column.weightToKg}kg
                                        </>
                                      )}
                                    {band.rushHourSurchargeCents != null && (
                                      <>
                                        {" · rush +"}
                                        {formatCents(
                                          band.rushHourSurchargeCents
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="absolute -right-1 -top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                                    <Button
                                      size="icon-xs"
                                      variant="ghost"
                                      aria-label={`Edit rate band for ${lane.label}, ${bracket}`}
                                      onClick={() => onEditBand(band)}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      size="icon-xs"
                                      variant="ghost"
                                      aria-label={`Delete rate band for ${lane.label}, ${bracket}`}
                                      disabled={remove.isPending}
                                      onClick={() =>
                                        requestDelete(band, lane.label, bracket)
                                      }
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-auto w-full gap-1 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                                  aria-label={`Add rate for ${lane.label.replace(" → ", " to ")}, ${bracket}`}
                                  onClick={() =>
                                    onEditBand(null, {
                                      fromServiceAreaId: lane.from.id,
                                      toServiceAreaId: lane.to.id,
                                      weightFromKg: column.weightFromKg,
                                      weightToKg: column.weightToKg,
                                    })
                                  }
                                >
                                  <Plus className="h-3 w-3" />
                                  Add rate
                                </Button>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <div className="border-t px-2 py-1.5">
          <Button
            variant="ghost"
            size="sm-touch"
            className="text-primary hover:text-primary"
            onClick={() => onEditBand(null)}
            title="Add a label rate for an origin and destination lane"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add lane rate
          </Button>
        </div>
      </Card>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rate band?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `${deleteTarget.lane}, ${deleteTarget.bracket} will stop matching new quotes immediately.`
                : "This rate band will stop matching new quotes immediately."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {actionError && (
            <p role="alert" className="text-sm text-destructive">
              {actionError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={(event) => {
                event.preventDefault();
                confirmDelete();
              }}
            >
              {remove.isPending ? "Deleting…" : "Delete rate band"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function BandPriceSummary({ band }: { band: CarrierRateBandRow }) {
  const anchor = Number(band.anchorWeightKg ?? band.weightFromKg);
  return (
    <div className="mt-0.5 text-xs">
      <span className="font-medium">{formatCents(band.baseRateCents)}</span>
      {band.perKgCents !== null && (
        <span className="text-muted-foreground">
          {` + ${formatCents(band.perKgCents)}/kg from ${anchor} kg`}
        </span>
      )}
      {band.rushHourSurchargeCents !== null && (
        <span className="text-muted-foreground">
          {` · rush +${formatCents(band.rushHourSurchargeCents)}`}
        </span>
      )}
      <span className="ml-1 text-[10px] text-muted-foreground">
        {band.currencyCode}
      </span>
    </div>
  );
}

function PickupBandsTable({ rows }: { rows: PickupRateBandRow[] }) {
  const emptyLabel =
    "No pickup rate for this lane — no pickup option is offered.";
  return (
    <TableShell
      title="Pickup rates"
      note="Sender to dispatch centre. Set by EZX."
      action={
        // These rates are read-only here; the service that owns them is where
        // they are actually configured. Saying so beats leaving a dead end.
        <Button asChild variant="ghost" size="sm" className="gap-1.5">
          <Link to="/services/pickup">
            Pickup services <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      }
      mobile={
        rows.length === 0 ? (
          <MobileEmpty label={emptyLabel} />
        ) : (
          rows.map((row) => (
            <article
              key={row.id}
              aria-label={`Pickup rate from ${row.fromServiceArea.name} to ${row.dispatchCenter.name}`}
              className="space-y-1.5 p-3 text-xs"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {row.fromServiceArea.name} → {row.dispatchCenter.name}
                  </p>
                  <p className="truncate text-muted-foreground">
                    {row.carrierAddOnService?.name ?? "-"}
                  </p>
                </div>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatCents(row.baseRateCents)}
                </span>
              </div>
              <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-muted-foreground">
                <Weight from={row.weightFromKg} to={row.weightToKg} />
                <span>
                  {row.perKgCents === null
                    ? "Flat"
                    : `Per kg ${formatCents(row.perKgCents)} from ${Number(row.anchorWeightKg ?? row.weightFromKg)} kg`}
                  {row.rushHourSurchargeCents !== null &&
                  row.rushHourSurchargeCents !== undefined
                    ? ` · rush +${formatCents(row.rushHourSurchargeCents)}`
                    : ""}
                  {` · ${row.currencyCode}`}
                </span>
              </div>
            </article>
          ))
        )
      }
      head={
        <>
          <TableHead>From</TableHead>
          <TableHead>Dispatch centre</TableHead>
          <TableHead>Service</TableHead>
          <TableHead>Package billable kg</TableHead>
          <TableHead>Anchor</TableHead>
          <TableHead className="text-right">Base / shipment</TableHead>
          <TableHead className="text-right">Per kg</TableHead>
          <TableHead className="text-right">Rush</TableHead>
          <TableHead>Currency</TableHead>
        </>
      }
    >
      {rows.length === 0 ? (
        <EmptyRow span={9} label={emptyLabel} />
      ) : (
        rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="font-medium">
              <Lane from={r.fromServiceArea} />
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {r.dispatchCenter.name}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              {r.carrierAddOnService?.name ?? "—"}
            </TableCell>
            <TableCell>
              <Weight from={r.weightFromKg} to={r.weightToKg} />
            </TableCell>
            <TableCell className="tabular-nums">
              {r.anchorWeightKg === null || r.anchorWeightKg === undefined
                ? `${Number(r.weightFromKg)} kg`
                : `${Number(r.anchorWeightKg)} kg`}
            </TableCell>
            <TableCell className="text-right">
              <Money cents={r.baseRateCents} />
            </TableCell>
            <TableCell className="text-right">
              <Money cents={r.perKgCents} />
            </TableCell>
            <TableCell className="text-right">
              <Money cents={r.rushHourSurchargeCents} />
            </TableCell>
            <TableCell>{r.currencyCode}</TableCell>
          </TableRow>
        ))
      )}
    </TableShell>
  );
}

function RedistributionBandsTable({
  rows,
}: {
  rows: RedistributionRateBandRow[];
}) {
  const emptyLabel =
    "No redistribution rate for this lane — no pickup option is offered.";
  return (
    <TableShell
      title="Redistribution rates"
      note="Dispatch centre to your intake. Set by EZX."
      mobile={
        rows.length === 0 ? (
          <MobileEmpty label={emptyLabel} />
        ) : (
          rows.map((row) => (
            <article
              key={row.id}
              aria-label={`Redistribution rate from ${row.dispatchCenter.name}`}
              className="space-y-1.5 p-3 text-xs"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 truncate text-sm font-medium">
                  {row.dispatchCenter.name}
                </p>
                <span className="shrink-0 font-medium tabular-nums">
                  {formatCents(row.baseRateCents)}
                </span>
              </div>
              <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-muted-foreground">
                <Weight from={row.weightFromKg} to={row.weightToKg} />
                <span>
                  {row.perKgCents === null
                    ? "Flat"
                    : `Per kg ${formatCents(row.perKgCents)} from ${Number(row.anchorWeightKg ?? row.weightFromKg)} kg`}
                  {row.rushHourSurchargeCents !== null &&
                  row.rushHourSurchargeCents !== undefined
                    ? ` · rush +${formatCents(row.rushHourSurchargeCents)}`
                    : ""}
                  {` · ${row.currencyCode}`}
                </span>
              </div>
            </article>
          ))
        )
      }
      head={
        <>
          <TableHead>Dispatch centre</TableHead>
          <TableHead>Package billable kg</TableHead>
          <TableHead>Anchor</TableHead>
          <TableHead className="text-right">Base / shipment</TableHead>
          <TableHead className="text-right">Per kg</TableHead>
          <TableHead className="text-right">Rush</TableHead>
          <TableHead>Currency</TableHead>
        </>
      }
    >
      {rows.length === 0 ? (
        <EmptyRow span={7} label={emptyLabel} />
      ) : (
        rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell className="font-medium">
              {r.dispatchCenter.name}
            </TableCell>
            <TableCell>
              <Weight from={r.weightFromKg} to={r.weightToKg} />
            </TableCell>
            <TableCell className="tabular-nums">
              {r.anchorWeightKg === null || r.anchorWeightKg === undefined
                ? `${Number(r.weightFromKg)} kg`
                : `${Number(r.anchorWeightKg)} kg`}
            </TableCell>
            <TableCell className="text-right">
              <Money cents={r.baseRateCents} />
            </TableCell>
            <TableCell className="text-right">
              <Money cents={r.perKgCents} />
            </TableCell>
            <TableCell className="text-right">
              <Money cents={r.rushHourSurchargeCents} />
            </TableCell>
            <TableCell>{r.currencyCode}</TableCell>
          </TableRow>
        ))
      )}
    </TableShell>
  );
}

/**
 * These rows are EZX's fee, not the carrier's own charges — the panel used to
 * be titled "Surcharges → Service markups", which reads like something the
 * carrier levies. It isn't: it's what EZX adds on top of the carrier's rate to
 * get the customer's price. Carriers can see it (they can back it out of a
 * customer quote anyway, and once payouts land it's the second line of their
 * statement), but it should say what it is.
 */
function SurchargesTab() {
  const { data, isLoading, error, refetch } = useCarrierSurcharges();
  if (isLoading) return <LoadingRow label="surcharges" />;
  if (error) {
    return (
      <ErrorRow label="surcharges" error={error} onRetry={() => refetch()} />
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        What EZX adds on top of your rate to reach the customer's price. Set by
        EZX — you keep your rate; this is the platform's fee.
      </div>
      <PlatformFeeTable rows={data.platformFees} />
      {/*
        Property-type surcharges are NOT shown. They are EZX's config, not the
        carrier's, and nothing in the quote path reads them — `quoteShipment`
        never computes a property surcharge, so `propertyTypeSurchargeCents` is
        always 0. Rendering the table would tell a carrier that house-vs-condo
        pricing is live when it is not.
      */}
    </div>
  );
}

/** The EZX fee, per service, as real numbers — not a key=value dump. */
function PlatformFeeTable({ rows }: { rows: PlatformFeeRow[] }) {
  return (
    <TableShell
      title="EZX fee, per service"
      note="Percentage of your rate, plus a flat fee per shipment. Both apply."
      mobile={
        rows.length === 0 ? (
          <MobileEmpty label="No services." />
        ) : (
          rows.map((row) => {
            if (row.percent === null || row.flatCents === null) {
              return (
                <article
                  key={row.carrierServiceId}
                  aria-label={`EZX fee for ${row.name}`}
                  className="space-y-1 p-3"
                >
                  <p className="text-sm font-medium">{row.name}</p>
                  <p className="text-xs text-destructive">
                    No fee set — EZX cannot quote this service. Contact EZX.
                  </p>
                </article>
              );
            }
            const fee = Math.round(2000 * (row.percent / 100)) + row.flatCents;
            return (
              <article
                key={row.carrierServiceId}
                aria-label={`EZX fee for ${row.name}`}
                className="space-y-1.5 p-3 text-xs"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium">{row.name}</p>
                  <span className="tabular-nums">
                    {row.percent}% + {formatCents(row.flatCents)}
                  </span>
                </div>
                <p className="text-muted-foreground tabular-nums">
                  On $20.00: you keep $20.00 · EZX takes {formatCents(fee)}
                </p>
              </article>
            );
          })
        )
      }
      head={
        <>
          <TableHead>Service</TableHead>
          <TableHead className="text-right">% of your rate</TableHead>
          <TableHead className="text-right">Flat fee</TableHead>
          <TableHead>On a $20 shipment</TableHead>
        </>
      }
    >
      {rows.length === 0 ? (
        <EmptyRow span={4} label="No services." />
      ) : (
        rows.map((r) => {
          const { percent, flatCents } = r;
          // No fee at either level = this service cannot be quoted at all.
          // Say so, rather than rendering a blank that reads as "free".
          if (percent === null || flatCents === null) {
            return (
              <TableRow key={r.carrierServiceId}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right tabular-nums">—</TableCell>
                <TableCell className="text-right tabular-nums">—</TableCell>
                <TableCell className="text-sm">
                  <span className="text-destructive">
                    No fee set — EZX cannot quote this service. Contact EZX.
                  </span>
                </TableCell>
              </TableRow>
            );
          }
          const fee = Math.round(2000 * (percent / 100)) + flatCents;
          return (
            <TableRow key={r.carrierServiceId}>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="text-right tabular-nums">
                {percent}%
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {formatCents(flatCents)}
              </TableCell>
              <TableCell className="text-sm">
                <span className="text-muted-foreground tabular-nums">
                  you keep $20.00 · EZX takes {formatCents(fee)}
                </span>
              </TableCell>
            </TableRow>
          );
        })
      )}
    </TableShell>
  );
}

function QuotePreviewPanel({
  carrierServiceId,
  pickupServiceId,
  dispatchCenterId,
  destinationCarrierId,
  serviceAreas,
  serviceAreasLoading,
  fromServiceAreaId,
  onFromServiceAreaChange,
  toServiceAreaId,
  onToServiceAreaChange,
}: {
  carrierServiceId: string;
  pickupServiceId: string;
  dispatchCenterId: string;
  destinationCarrierId: string;
  serviceAreas: CarrierServiceAreaRow[];
  serviceAreasLoading: boolean;
  fromServiceAreaId: string;
  onFromServiceAreaChange: (value: string) => void;
  toServiceAreaId: string;
  onToServiceAreaChange: (value: string) => void;
}) {
  const preview = usePreviewCarrierPickupQuote();
  const previewLock = useRef(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [form, setForm] = useState({
    actualWeightKg: "1",
    lengthCm: "30",
    widthCm: "20",
    heightCm: "15",
  });

  const setField = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canPreview =
    carrierServiceId &&
    pickupServiceId &&
    dispatchCenterId &&
    destinationCarrierId &&
    fromServiceAreaId &&
    toServiceAreaId;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canPreview || previewLock.current) return;
    setValidationError(null);
    const dimensions = {
      actualWeightKg: Number(form.actualWeightKg),
      lengthCm: Number(form.lengthCm),
      widthCm: Number(form.widthCm),
      heightCm: Number(form.heightCm),
    };
    if (
      Object.values(dimensions).some(
        (value) => !Number.isFinite(value) || value <= 0
      )
    ) {
      setValidationError(
        "Weight and all dimensions must be greater than zero."
      );
      return;
    }
    previewLock.current = true;
    preview.mutate(
      {
        carrierServiceId,
        carrierAddOnServiceId: pickupServiceId,
        dispatchCenterId,
        destinationCarrierId,
        fromServiceAreaId,
        toServiceAreaId,
        ...dimensions,
      },
      {
        onSettled: () => {
          previewLock.current = false;
        },
      }
    );
  };

  return (
    <Card className="border-blue-200 bg-blue-50/20">
      <CardContent className="p-3 sm:p-4">
        <form
          onSubmit={submit}
          className="grid items-end gap-4 xl:grid-cols-[minmax(0,1fr)_360px]"
        >
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-blue-700">
              <Calculator className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="text-sm font-medium">Quote preview</p>
                <p className="text-xs font-normal">
                  Runs the customer quote engine for a sample Shipment (CAD).
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <AreaField
                label="From area"
                value={fromServiceAreaId}
                onChange={onFromServiceAreaChange}
                areas={serviceAreas}
                loading={serviceAreasLoading}
              />
              <AreaField
                label="To area"
                value={toServiceAreaId}
                onChange={onToServiceAreaChange}
                areas={serviceAreas}
                loading={serviceAreasLoading}
              />
              {(
                [
                  ["actualWeightKg", "Weight kg"],
                  ["lengthCm", "L cm"],
                  ["widthCm", "W cm"],
                  ["heightCm", "H cm"],
                ] as const
              ).map(([key, label]) => (
                <label
                  key={key}
                  htmlFor={`pricing-preview-${key}`}
                  className="space-y-1 text-xs font-medium"
                >
                  <span>{label}</span>
                  <Input
                    id={`pricing-preview-${key}`}
                    type="number"
                    min="0.001"
                    step="any"
                    required
                    className="bg-background"
                    value={form[key]}
                    inputMode="decimal"
                    onChange={(event) => setField(key, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-[1fr_auto] xl:grid-cols-1">
            <div className="rounded-md border bg-background/80 p-3 text-sm">
              {!preview.data ? (
                <p className="text-muted-foreground">No preview yet.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                  <PreviewRow
                    label="Label billable kg"
                    value={preview.data.labelBillableKg.toFixed(2)}
                  />
                  <PreviewRow
                    label="Pickup billable kg"
                    value={preview.data.pickupBillableKg.toFixed(2)}
                  />
                  <PreviewRow
                    label="Label leg"
                    value={formatCents(preview.data.labelLegCents)}
                  />
                  <PreviewRow
                    label="Pickup leg"
                    value={formatCents(preview.data.pickupLegCents)}
                  />
                  <PreviewRow
                    label="Redistribution"
                    value={formatCents(preview.data.redistLegCents)}
                  />
                  <PreviewRow
                    label="Pickup total"
                    value={formatCents(preview.data.totalPickupCents)}
                  />
                  {preview.data.labelMissing && (
                    <p className="text-xs text-destructive">
                      Label rate missing.
                    </p>
                  )}
                  {preview.data.pickupError && (
                    <p className="text-xs text-destructive">
                      {preview.data.pickupError.message}
                    </p>
                  )}
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={preview.isPending || !canPreview}
              size="touch"
            >
              {preview.isPending && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Preview
            </Button>
            {validationError && (
              <p role="alert" className="text-sm text-destructive">
                {validationError}
              </p>
            )}
            {preview.isError && (
              <p role="alert" className="text-sm text-destructive">
                Preview failed.
              </p>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function AreaField({
  label,
  value,
  onChange,
  areas,
  loading,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  areas: CarrierServiceAreaRow[];
  loading: boolean;
}) {
  const fieldId = `pricing-preview-${label.toLowerCase().replace(/\s+/g, "-")}`;
  if (areas.length > 0) {
    return (
      <div className="space-y-1 text-xs font-medium md:col-span-2">
        <span>{label}</span>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger
            aria-label={label}
            size="sm"
            className="bg-background text-sm"
          >
            <SelectValue placeholder={label} />
          </SelectTrigger>
          <SelectContent>
            {areas.map((area) => (
              <SelectItem key={area.id} value={area.id}>
                {area.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <label
      htmlFor={fieldId}
      className="space-y-1 text-xs font-medium md:col-span-2"
    >
      <span>{label}</span>
      <Input
        id={fieldId}
        className="bg-background"
        value={value}
        placeholder={loading ? "Loading areas..." : "Service area ID"}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

function formatCents(value: number | null): string {
  return value == null ? "-" : `$${(value / 100).toFixed(2)}`;
}

type DispatchCenterOption =
  CarrierMeResponse["carriers"][number]["allowedDispatchCenters"][number];

function ensureSelected<T extends { id: string }>(
  rows: T[],
  value: string,
  setValue: (next: string) => void
) {
  if (rows.length === 0) {
    if (value) setValue("");
    return;
  }
  if (!value || !rows.some((row) => row.id === value)) {
    setValue(rows[0].id);
  }
}

function getDispatchCenters(
  carriers: CarrierMeResponse["carriers"]
): DispatchCenterOption[] {
  const byId = new Map<string, DispatchCenterOption>();
  for (const carrier of carriers) {
    for (const center of carrier.allowedDispatchCenters) {
      if (center.isActive) byId.set(center.id, center);
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    (a.name ?? a.code).localeCompare(b.name ?? b.code)
  );
}

function getServiceAreaRows(
  data: CarrierServiceAreaRow[] | { data?: CarrierServiceAreaRow[] } | undefined
): CarrierServiceAreaRow[] {
  if (Array.isArray(data)) return data;
  return data?.data ?? [];
}

function formatServiceLabel(row: PricingRow): string {
  const carrierName = readNestedString(row, "carrier", "name");
  const name = readRowString(row, ["name", "code", "id"]) ?? "Service";
  return carrierName ? `${carrierName} → ${name}` : name;
}

function formatUnitValue(row: PricingRow, key: string, unit: string): string {
  const value = readRowString(row, [key]);
  return value ? `${value} ${unit}` : "-";
}

function getCarrierId(row: PricingRow | null): string | null {
  return (
    readRowString(row, ["carrierId"]) ?? readNestedString(row, "carrier", "id")
  );
}

function readRowString(row: PricingRow | null | undefined, keys: string[]) {
  if (!row) return null;
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
  }
  return null;
}

function readNestedString(
  row: PricingRow | null | undefined,
  key: string,
  nestedKey: string
) {
  if (!row) return null;
  const nested = row[key];
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) {
    return null;
  }
  const value = (nested as Record<string, unknown>)[nestedKey];
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
}
